import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { Link } from 'react-router-dom'
import Panel from '../ui/Panel'
import Avatar from '../ui/Avatar'
import './dashboard.css'

/*
 * The five skills a scout judges a player on, and who leads each one.
 *
 * The five are not written down here. The API reads them from Sports.Tags, so
 * basketball returns Percentage FT / 2pt / 3pt, Ball handling and Speed, while
 * football returns Shot, Dribble, Head, Control and Speed — and a sport added
 * later arrives on its own.
 *
 * Five leaderboards side by side would be five columns of three names, which is
 * a lot of furniture for very little. One board with the skill picked above it
 * shows five players properly, and switching costs a click with no new request:
 * everything is in the single response.
 */
export default function DashboardSkills() {
  const [stats, setStats] = useState([])
  const [sport, setSport] = useState('')
  const [active, setActive] = useState(0)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = () => {
    axios.get(
      process.env.REACT_APP_API_SERVER + 'manager/dashboard/skillstats/' +
      sessionStorage.getItem('SportID') + '/' + sessionStorage.getItem('userToken'),
      { headers: { 'Authorization': 'Bearer ' + sessionStorage.getItem('mngapiToken') } }
    ).then(res => {
      const payload = res.data && res.data.results ? res.data.results : {}
      setStats(Array.isArray(payload.stats) ? payload.stats : [])
      setSport(payload.sport || '')
      setIsLoading(false)
    }).catch(() => {
      setIsLoading(false)
    })
  }

  const current = stats[active] || null
  const players = current && Array.isArray(current.players) ? current.players : []

  /* Every one of these skills is recorded out of 100, so the bars are measured
     against 100 rather than against the best player in view. Scaling to the
     leader would make the top of a weak field look like the top of a strong
     one, and would stop the bars meaning the same thing from skill to skill. */
  const SCALE = 100

  const chips = stats.map((row, index) => (
    <button
      type="button"
      key={row.name}
      className={'skl-chip' + (index === active ? ' is-on' : '')}
      aria-pressed={index === active}
      onClick={() => setActive(index)}
    >
      <span className="skl-chip-name">{row.name}</span>
      <span className="skl-chip-val num">{row.best}</span>
    </button>
  ))

  const board = players.map((item, index) => {
    const value = Number(item.Value) || 0
    const pct = Math.max(0, Math.min(100, (value / SCALE) * 100))

    return (
      <Link className="skl-row" to={`/user/${item.UUID}`} key={item.UUID || index}>
        <div className="rank num">{String(index + 1).padStart(2, '0')}</div>
        <Avatar uuid={item.UUID} name={item.Name} size={34} />
        <div className="skl-who">
          <div className="p-name">{item.Name}</div>
          <div className="skl-track">
            <div
              className="skl-fill"
              style={{ width: pct + '%', animationDelay: (0.05 + index * 0.06) + 's' }}
            />
          </div>
        </div>
        <div className="skl-val">
          <b className="num">{value}</b>
          <span className="mono">{item.Country}</span>
        </div>
      </Link>
    )
  })

  return (
    <Panel
      title="Skills"
      subtitle={sport ? `${sport} · leaders in each skill` : 'Leaders in each skill'}
    >
      {isLoading &&
        <div className="sk-list">
          <div className="skeleton sk-line"></div>
          <div className="skeleton sk-line"></div>
          <div className="skeleton sk-line short"></div>
        </div>
      }

      {!isLoading && stats.length === 0 &&
        <div className="empty">No skills are recorded for this sport yet.</div>
      }

      {!isLoading && stats.length > 0 &&
        <>
          <div className="skl-chips">{chips}</div>

          {players.length === 0 &&
            <div className="empty">No player has been rated on {current.name} yet.</div>
          }

          {players.length > 0 &&
            <>
              <div className="skl-list scroll-body">{board}</div>
              {/* A leader is only as impressive as the field behind it, so the
                  size of that field is stated rather than left to be assumed. */}
              <p className="callout">
                Rated out of 100. <b>{current.rated}</b>
                {current.rated === 1 ? ' player carries ' : ' players carry '}
                a {current.name} rating, averaging <b>{current.average}</b>.
              </p>
            </>
          }
        </>
      }
    </Panel>
  )
}
